import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ReportType } from '@vision/ui/enums';
import {
  DEFAULT_REPORT_DETAIL_LOCKED_FILTERS,
  ReportDatePickerValue,
  ReportDateRangeType,
  ReportDetail,
  ReportDetailFilterOptions,
  ReportDetailLockedFilterMap,
  ReportWidget,
  ReportWidgetLayoutRequest,
} from '@vision/ui/interfaces';
import {
  DEFAULT_REPORT_DATE_FORMAT,
  DEFAULT_REPORT_DATE_PICKER_VALUE,
} from '@vision/ui/pages/ReportsDetailGraphic/constants';
import { ReportsDetailService } from '@vision/ui/services';
import {
  EMPTY_REPORT_DETAIL_MAIN_FILTERS,
  getReportDetailLockedFiltersMap,
  getReportDetailMainFilters,
  type ReportDetailMainFilters,
  normalizeReportDetailFilters,
  serializeReportDetailMainFilters,
} from '../../../utils/reports/report-detail-filter-lock.util';
import { insertIfObject } from '@vision/ui/utils/object.utils';
import { normalizeTicketReportingFilters } from '@vision/ui/utils/reports/ticket-reporting-filter.util';
import dayjs from 'dayjs';

const getVoiceDateFormat = (date: string | undefined, defaultValue: string): string => {
  if (date && dayjs(date, DEFAULT_REPORT_DATE_FORMAT, true).isValid()) {
    return dayjs(date).format(DEFAULT_REPORT_DATE_FORMAT);
  }

  return defaultValue;
};

function checkReportDetailFilters(detail?: ReportDetailFilterOptions): ReportDetailFilterOptions {
  const filteredDetail = normalizeReportDetailFilters(detail);

  return {
    ...filteredDetail,
    from_date: getVoiceDateFormat(filteredDetail.from_date, DEFAULT_REPORT_DATE_PICKER_VALUE.fromDate),
    to_date: getVoiceDateFormat(filteredDetail.to_date, DEFAULT_REPORT_DATE_PICKER_VALUE.toDate),
  };
}

interface ReportsDetailStateType {
  reportDetail?: ReportDetail;
  widgets?: ReportWidget[];
  selectedDate: ReportDatePickerValue;
  changedToDateRange: number;
  selectedMainFilters: ReportDetailMainFilterType;
  lockedMainFilters: ReportDetailLockedFilterMap;
  activeResizingWidgetId?: string;
  widgetLoadStatus: {
    [key: string]: boolean;
  };
  isExporting: boolean;
  jobResults?: any;
  headerSettings: {
    logoActive: boolean;
    titleActive: boolean;
    descriptionActive: boolean;
  };
}

type ReportDetailMainFilterType = ReportDetailMainFilters;

const initialState: ReportsDetailStateType = {
  reportDetail: null,
  widgets: [],
  selectedDate: DEFAULT_REPORT_DATE_PICKER_VALUE,
  changedToDateRange: 0,
  selectedMainFilters: {
    ...EMPTY_REPORT_DETAIL_MAIN_FILTERS,
  },
  lockedMainFilters: { ...DEFAULT_REPORT_DETAIL_LOCKED_FILTERS },
  activeResizingWidgetId: undefined,
  widgetLoadStatus: {},
  isExporting: false,
  jobResults: null,
  headerSettings: {
    logoActive: true,
    titleActive: true,
    descriptionActive: true,
  },
};

export const ReportsDetailState = createSlice({
  name: 'ReportsDetailState',
  initialState,
  reducers: {
    updateReportDetailTitleAction: (state, { payload }: PayloadAction<string>) => {
      if (state.reportDetail) {
        state.reportDetail.title = payload;
      }
    },

    updateDateRangeAction: (state, action) => {
      state.selectedDate = action.payload;
      if (state.reportDetail) {
        state.reportDetail.detail = serializeReportDetailMainFilters({
          detail: state.reportDetail.detail,
          date: action.payload,
          filters: state.selectedMainFilters,
          isTicketReport: state.reportDetail.type === ReportType.INNER_LOOP,
          locks: state.lockedMainFilters,
        });
      }
      state.changedToDateRange = state.changedToDateRange + 1;
    },

    updateReportMainFiltersAction: (state, { payload }: PayloadAction<ReportDetailMainFilterType>) => {
      if (!state.reportDetail) {
        return;
      }

      const {
        selectedChannelIds,
        selectedUnitIds,
        selectedFlowIds,
        selectedTagIds,
        selectedCustomerSchemaIds,
        selectedCustomerSchemaValues,
      } = payload;

      // Update main filters state
      state.selectedMainFilters = {
        selectedChannelIds: selectedChannelIds ?? [],
        selectedUnitIds: selectedUnitIds ?? [],
        selectedFlowIds: selectedFlowIds ?? [],
        selectedTagIds: selectedTagIds ?? [],
        selectedCustomerSchemaIds: selectedCustomerSchemaIds ?? [],
        selectedCustomerSchemaValues: selectedCustomerSchemaValues ?? [],
        ...normalizeTicketReportingFilters(payload),
      };

      state.reportDetail.detail = serializeReportDetailMainFilters({
        detail: state.reportDetail.detail,
        date: state.selectedDate,
        filters: state.selectedMainFilters,
        isTicketReport: state.reportDetail.type === ReportType.INNER_LOOP,
        locks: state.lockedMainFilters,
      });
    },

    updateReportLockedMainFiltersAction: (state, { payload }: PayloadAction<ReportDetailLockedFilterMap>) => {
      state.lockedMainFilters = payload;

      if (state.reportDetail) {
        state.reportDetail.detail = serializeReportDetailMainFilters({
          detail: state.reportDetail.detail,
          date: state.selectedDate,
          filters: state.selectedMainFilters,
          isTicketReport: state.reportDetail.type === ReportType.INNER_LOOP,
          locks: payload,
        });
      }
    },

    // hem updateDateRangeAction i hemde  updateReportMainFiltersAction çalıştıran bir action
    updateReportAllFiltersAction: (
      state,
      { payload }: PayloadAction<{ date: ReportDatePickerValue; filters: ReportDetailMainFilterType }>,
    ) => {
      const hasDateChanged =
        state.selectedDate.fromDate !== payload.date.fromDate ||
        state.selectedDate.toDate !== payload.date.toDate ||
        state.selectedDate.dateRange !== payload.date.dateRange;

      state.selectedDate = payload.date;
      state.selectedMainFilters = payload.filters;

      if (hasDateChanged) {
        state.changedToDateRange = state.changedToDateRange + 1;
      }

      if (state.reportDetail) {
        state.reportDetail.detail = serializeReportDetailMainFilters({
          detail: state.reportDetail.detail,
          date: payload.date,
          filters: payload.filters,
          isTicketReport: state.reportDetail.type === ReportType.INNER_LOOP,
          locks: state.lockedMainFilters,
        });
      }
    },

    updateReportsDetailLayoutAction: (state, { payload }: PayloadAction<ReportWidgetLayoutRequest[]>) => {
      state.widgets = state.widgets.map((widget: ReportWidget) => {
        const layout = payload.find((layout: ReportWidgetLayoutRequest) => layout.id === widget.id);
        if (layout) {
          return {
            ...widget,
            layout: layout.layout,
          };
        }
        return widget;
      });
    },

    setActiveResizingWidgetIdAction: (state, { payload }: PayloadAction<string>) => {
      state.activeResizingWidgetId = payload;
    },

    setIsExportingAction: (state, { payload }: PayloadAction<boolean>) => {
      state.isExporting = payload;
    },

    setChartFinishedAction: (state, { payload }: PayloadAction<string>) => {
      state.widgetLoadStatus[payload] = true;
    },

    setJobResultsAction: (state, { payload }: PayloadAction<any>) => {
      state.jobResults = payload;
    },

    updateHeaderSettingsAction: (
      state,
      { payload }: PayloadAction<{ logoActive?: boolean; titleActive?: boolean; descriptionActive?: boolean }>,
    ) => {
      state.headerSettings = { ...state.headerSettings, ...payload };
    },

    setLogoActiveAction: (state, { payload }: PayloadAction<boolean>) => {
      state.headerSettings.logoActive = payload;
    },

    setTitleActiveAction: (state, { payload }: PayloadAction<boolean>) => {
      state.headerSettings.titleActive = payload;
    },

    setDescriptionActiveAction: (state, { payload }: PayloadAction<boolean>) => {
      state.headerSettings.descriptionActive = payload;
    },

    updateReportHeaderAction: (
      state,
      {
        payload,
      }: PayloadAction<{
        title?: { text: string; active: boolean };
        description?: { text: string; active: boolean };
        logo?: { active: boolean };
      }>,
    ) => {
      if (state.reportDetail) {
        state.reportDetail.header = {
          ...state.reportDetail.header,
          ...payload,
        };
      }
    },

    updateReportHeaderLogoAction: (state, { payload }: PayloadAction<string | null>) => {
      if (state.reportDetail) {
        state.reportDetail.header_logo = payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(ReportsDetailService.endpoints.getReport.matchFulfilled, (state, { payload }) => {
      const { detail, header } = payload.data;

      const selectedDate: ReportDatePickerValue = {
        ...DEFAULT_REPORT_DATE_PICKER_VALUE,
        ...insertIfObject(!!detail?.from_date, {
          fromDate: getVoiceDateFormat(detail?.from_date, DEFAULT_REPORT_DATE_PICKER_VALUE.fromDate),
        }),
        ...insertIfObject(!!detail?.to_date, {
          toDate: getVoiceDateFormat(detail?.to_date, DEFAULT_REPORT_DATE_PICKER_VALUE.toDate),
        }),
        ...insertIfObject(!!detail?.date_range, { dateRange: detail?.date_range as ReportDateRangeType }),
      };

      const headerSettings = {
        logoActive: header?.logo?.active ?? false,
        titleActive: header?.title?.active ?? false,
        descriptionActive: header?.description?.active ?? false,
      };

      // Update state in one assignment
      Object.assign(state, {
        reportDetail: {
          ...payload.data,
          latest_action_log: null, // not needed
          detail: checkReportDetailFilters(detail),
        },
        selectedDate,
        selectedMainFilters: getReportDetailMainFilters(detail, payload.data.type === ReportType.INNER_LOOP),
        lockedMainFilters: getReportDetailLockedFiltersMap(detail),
        headerSettings,
      });
    });
    builder.addMatcher(ReportsDetailService.endpoints.getReportWidgets.matchFulfilled, (state, { meta, payload }) => {
      if (meta.arg.originalArgs.skipWidgetSync) {
        return;
      }

      state.widgets = payload.data.map((widget) => ({
        ...widget,
        detail: {
          ...widget.detail,
          from_date: getVoiceDateFormat(
            widget.detail?.from_date ? widget.detail.from_date : state.selectedDate.fromDate,
            DEFAULT_REPORT_DATE_PICKER_VALUE.fromDate,
          ),
          to_date: getVoiceDateFormat(
            widget.detail?.to_date ? widget.detail.to_date : state.selectedDate.toDate,
            DEFAULT_REPORT_DATE_PICKER_VALUE.toDate,
          ),
        },
      }));
      state.widgetLoadStatus = state.widgets.reduce(
        (acc, widget) => {
          acc[widget.id] = false;
          return acc;
        },
        {} as Record<string, boolean>,
      );
    });
    builder.addMatcher(ReportsDetailService.endpoints.deleteReportWidget.matchFulfilled, (state, { payload }) => {
      state.widgets = state.widgets.filter((widget: ReportWidget) => widget.id !== payload.data.id); // Specify the type of widget
    });
    builder.addMatcher(ReportsDetailService.endpoints.updateReportWidget.matchFulfilled, (state, { payload }) => {
      state.widgets = state.widgets.map((widget: ReportWidget) => {
        if (widget.id === payload.data.id) {
          return {
            ...widget,
            ...payload.data,
          };
        }
        return widget;
      });
    });
    builder.addMatcher(ReportsDetailService.endpoints.copyReportWidget.matchFulfilled, (state, { payload, meta }) => {
      const copiedWidgetId = meta.arg.originalArgs?.widgetId;
      const copiedWidgetIndex = state.widgets.findIndex((widget: ReportWidget) => widget.id === copiedWidgetId);
      state.widgets.splice(copiedWidgetIndex + 1, 0, payload.data);
    });
  },
});

export const {
  updateReportDetailTitleAction,
  updateDateRangeAction,
  updateReportLockedMainFiltersAction,
  updateReportMainFiltersAction,
  updateReportAllFiltersAction,
  updateReportsDetailLayoutAction,
  setActiveResizingWidgetIdAction,
  setIsExportingAction,
  setChartFinishedAction,
  setJobResultsAction,
  updateHeaderSettingsAction,
  setLogoActiveAction,
  setTitleActiveAction,
  setDescriptionActiveAction,
  updateReportHeaderAction,
  updateReportHeaderLogoAction,
} = ReportsDetailState.actions;
